import userHasPermission from '@/helpers/userHasPermission'
import permissions from '@/constants/permissions'
import type { Recommendation } from '@/types'

import type { NextEditionDocumentName, ApiPermissionsFlags } from '@/types'

export const apiPermissionsFlags = () =>
  ({
    postCoordinatorFlag: userHasPermission(permissions.nextEditionRecommendationAddCoordinator),
    postCoordinatorFirst:
      userHasPermission(permissions.nextEditionRecommendationAddCoordinator) &&
      !userHasPermission(permissions.nextEditionRecommendationAdd),
    getCoordinatorFlag: userHasPermission(permissions.nextEditionRecommendationViewCoordinator),
    getCoordinatorFirst:
      userHasPermission(permissions.nextEditionRecommendationViewCoordinator) &&
      !userHasPermission(permissions.nextEditionRecommendationView)
  }) as const satisfies ApiPermissionsFlags

export const nextEditionDocumentNames = [
  'next_edition_coordinator_recommendation',
  'next_edition_application'
] as const

type PermissionFlags = {
  add: boolean
}
export const hasPermission = (type: NextEditionDocumentName): PermissionFlags => {
  const permission: Record<NextEditionDocumentName, PermissionFlags> = {
    next_edition_coordinator_recommendation: {
      add:
        userHasPermission(permissions.nextEditionRecommendationAdd) ||
        userHasPermission(permissions.nextEditionRecommendationAddCoordinator)
    },
    next_edition_application: {
      add: false
    }
  }
  return permission[type]
}

/**
 * Maps a key from Recommendation type to the corresponding nextEditionDocumentName
 * @param key A key from the Recommendation type that ends with "_status"
 * @returns The corresponding document name from nextEditionDocumentNames
 */
export const getDocumentNameFromRecommendationKey = (
  key: keyof Recommendation
): NextEditionDocumentName => {
  return key.replace('_status', '') as NextEditionDocumentName
}

/**
 * Checks if a recommendation can be previewed
 * @param recommendation The recommendation value to check
 * @returns True if the recommendation can be previewed
 */
export const canPreview = (recommendation: Recommendation[keyof Recommendation]) =>
  recommendation !== null

/**
 * Checks if a recommendation can be added
 * @param recommendation The recommendation value to check
 * @param key The key from the Recommendation type
 * @returns True if the recommendation can be added
 */
export const canAdd = (
  recommendation: Recommendation[keyof Recommendation],
  key: keyof Recommendation
) => {
  const formattedKey = getDocumentNameFromRecommendationKey(key)
  return hasPermission(formattedKey).add && !canPreview(recommendation)
}
